import React, {ChangeEvent, useEffect, useState} from 'react';
import {Box, TextField, Button, Paper, Typography} from '@mui/material';
import {useAppDispatch} from '../../../app/hooks';
import {closeButton, closeButtonInner, form, formInner, overlay} from "../../Players/components/playerFormStyles";
import CloseIcon from "@mui/icons-material/Close";
import {Controller, useForm} from "react-hook-form";
import {createClient, IClient, ICreateClientPayload, IUpdateClientPayload, updateClient} from "../clientsSlice";
import {isValidData} from "../../../helpers/isValidData";
import {toast} from "react-toastify";

interface IClientFormProps {
    editingClient: IClient | null,
    onCloseForm: () => void
}

export enum clientFormFields {
    NAME = "name",
    FACEBOOK = "facebook",
    LINKEDIN = "linkedIn",
    COMMENTS = "comments",
    HOME_TOWN = "hometown",
    COUNTRY = "country",
    TELEPHONE = "telephone",
    EMAIL = "email",
    WEBSITE = "webSite",
}

interface IClientFormData {
    name: string
    facebook: string,
    linkedIn: string,
    comments: string
    hometown: string,
    country: string,
    telephone: string,
    email: string,
    webSite: string
}


const ClientForm: React.FC<IClientFormProps> = ({editingClient, onCloseForm}) => {
    const [formData, setFormData] = useState<IClientFormData>({
        name: "",
        facebook: "",
        linkedIn: "",
        comments: "",
        hometown: "",
        country: "",
        telephone: "",
        email: "",
        webSite: ""
    });
    const {handleSubmit, control, reset} = useForm<IClientFormData>();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (editingClient) {
            const userFormData = {
                name: editingClient.name ? editingClient.name : "",
                facebook: editingClient.socialMedia ? editingClient.socialMedia.facebook : "",
                linkedIn: editingClient.socialMedia ? editingClient.socialMedia.linkedIn : "",
                comments: editingClient.socialMedia ? editingClient.socialMedia.comments : "",
                hometown: editingClient.contact ? editingClient.contact.hometown : "",
                country: editingClient.contact ? editingClient.contact.country : "",
                telephone: editingClient.contact ? editingClient.contact.telephon : "",
                email: editingClient.contact ? editingClient.contact.email : "",
                webSite: editingClient.contact ? editingClient.contact.webSite : "",
            }
            setFormData(userFormData);
            reset(userFormData);
        }

    }, [editingClient])


    const handleChange = (field: keyof IClientFormData, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({...formData, [field]: e.target.value});
    };

    const onSubmit = () => {
        if (!isValidData(formData)) {
            toast.warning("SDC: Fill the form")
            return;
        }
        if (!editingClient) {
            const reqBody: ICreateClientPayload = {
                payload: {
                    name: formData.name,
                    socialMedia: {
                        facebook: formData.facebook,
                        linkedIn: formData.linkedIn,
                        comments: formData.comments
                    },
                    contact: {
                        hometown: formData.hometown,
                        country: formData.country,
                        telephon: formData.telephone,
                        email: formData.email,
                        webSite: formData.webSite
                    }
                },
                closeForm: onCloseForm
            }

            dispatch(createClient(reqBody));

        } else {
            const reqBody: IUpdateClientPayload = {
                payload: {
                    id: editingClient.id,
                    socialMedia: {
                        facebook: formData.facebook,
                        linkedIn: formData.linkedIn,
                        comments: formData.comments
                    },
                    contact: {
                        hometown: formData.hometown,
                        country: formData.country,
                        telephon: formData.telephone,
                        email: formData.email,
                        webSite: formData.webSite
                    }
                },
                closeForm: onCloseForm
            }
            dispatch(updateClient(reqBody));
        }
    };

    return (
        <Box sx={overlay}>
            <Box>
                <Paper sx={formInner}>
                    <Box sx={closeButtonInner}>
                        <Button
                            sx={closeButton}
                            onClick={onCloseForm}
                        >
                            <CloseIcon sx={{width: 20}}/>
                        </Button>
                    </Box>
                    <Typography variant="h5" align="center">
                        {editingClient ? "Edit Client" : "Create client"}
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box sx={form}>
                            <Controller
                                control={control}
                                name={clientFormFields.NAME}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Name"
                                        value={formData.name}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.NAME, e)
                                        }}
                                        error={Boolean(errors.facebook)}
                                        helperText={
                                            errors.facebook && errors.facebook.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.FACEBOOK}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Facebook"
                                        value={formData.facebook}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.FACEBOOK, e)
                                        }}
                                        error={Boolean(errors.facebook)}
                                        helperText={
                                            errors.facebook && errors.facebook.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.facebook && errors.facebook.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.LINKEDIN}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="LinkedIn"
                                        value={formData.linkedIn}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.LINKEDIN, e)
                                        }}
                                        error={Boolean(errors.linkedIn)}
                                        helperText={
                                            errors.linkedIn && errors.linkedIn.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.linkedIn && errors.linkedIn.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.linkedIn && errors.linkedIn.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.COMMENTS}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Comments"
                                        value={formData.comments}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.COMMENTS, e)
                                        }}
                                        error={Boolean(errors.comments)}
                                        helperText={
                                            errors.comments && errors.comments.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.comments && errors.comments.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.comments && errors.comments.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.COUNTRY}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Country"
                                        value={formData.country}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.COUNTRY, e)
                                        }}
                                        error={Boolean(errors.country)}
                                        helperText={
                                            errors.country && errors.country.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.country && errors.country.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.country && errors.country.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.HOME_TOWN}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Home town"
                                        value={formData.hometown}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.HOME_TOWN, e)
                                        }}
                                        error={Boolean(errors.hometown)}
                                        helperText={
                                            errors.hometown && errors.hometown.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.hometown && errors.hometown.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.hometown && errors.hometown.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.TELEPHONE}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Telephone"
                                        value={formData.telephone}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.TELEPHONE, e)
                                        }}
                                        error={Boolean(errors.telephone)}
                                        helperText={
                                            errors.telephone && errors.telephone.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.telephone && errors.telephone.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.telephone && errors.telephone.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.WEBSITE}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Website"
                                        value={formData.webSite}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.WEBSITE, e)
                                        }}
                                        error={Boolean(errors.webSite)}
                                        helperText={
                                            errors.webSite && errors.webSite.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.webSite && errors.webSite.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.webSite && errors.webSite.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name={clientFormFields.EMAIL}
                                rules={{
                                    minLength: 2,
                                    maxLength: 150,
                                    pattern: {
                                        value: /[^\s]/,
                                        message: "Field can not contain only spaces"
                                    }
                                }}
                                render={({
                                             field: {onChange},
                                             formState: {errors},
                                         }) => (
                                    <TextField
                                        label="Email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            onChange(e)
                                            handleChange(clientFormFields.EMAIL, e)
                                        }}
                                        error={Boolean(errors.email)}
                                        helperText={
                                            errors.email && errors.email.type === "maxLength"
                                            && <span>This field can not contain more than 150 symbols</span>
                                            ||
                                            errors.email && errors.email.type === "minLength"
                                            && <span>This field can not contain less than 2 symbols</span>
                                            ||
                                            errors.email && errors.email.type === "pattern"
                                            && <span>Field can not contain only spaces</span>
                                        }
                                    />
                                )}
                            />
                            <Button variant="contained" type="submit">
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default ClientForm;
